import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, CheckCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    background: '',
    concern: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch('https://formspree.io/f/YOUR_FORM_ID', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          background: formData.background,
          message: formData.concern,
          _subject: `New enquiry from ${formData.fullName} - Vitae Careers`,
        }),
      });
      if (response.ok) {
        setIsSuccess(true);
        setFormData({ fullName: '', phone: '', email: '', background: '', concern: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = data.errors?.map((err) => err.message).join(', ') || 'Something went wrong. Please try again or email us directly.';
        setError(msg);
      }
    } catch {
      setError('Could not reach the server. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <section className="Contact_hero__iKDPq">
        <div className="container Contact_heroContainer__CknrB">
          <motion.span
            className="section-label"
            style={{ color: 'var(--c-gold)' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            GET IN TOUCH
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Contact Us
          </motion.h1>
          <motion.p
            className="Contact_heroSubtext__3v0-j"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Have a question? Ready to book your session? We're here to help guide your next steps.
          </motion.p>
        </div>
      </section>

      <section className="section">
        <div className="container Contact_contactLayout__r8p7i">
          <div className="Contact_infoCol__bBWQW">
            <div className="Contact_infoCard__qjKO4">
              <div className="Contact_iconWrap__eG97J">
                <Phone size={24} />
              </div>
              <div>
                <h4>Phone</h4>
                <p>
                  <a href="tel:+919335879877" style={{ color: 'inherit' }}>
                    +91 9335879877
                  </a>
                </p>
                <span className="Contact_meta__TOiAi">Call or WhatsApp</span>
              </div>
            </div>
            <div className="Contact_infoCard__qjKO4">
              <div className="Contact_iconWrap__eG97J">
                <Mail size={24} />
              </div>
              <div>
                <h4>Email</h4>
                <p>
                  <a href="mailto:careervitae2025@gmail.com" style={{ color: 'inherit' }}>
                    careervitae2025@gmail.com
                  </a>
                </p>
                <span className="Contact_meta__TOiAi">We reply within 24 hours</span>
              </div>
            </div>
            <div className="Contact_infoCard__qjKO4">
              <div className="Contact_iconWrap__eG97J">
                <MapPin size={24} />
              </div>
              <div>
                <h4>Address</h4>
                <p>C-703, Tower-C, The Grace, Sector-B, Sushant Golf City, Lucknow-226030</p>
                <span className="Contact_meta__TOiAi">In-person sessions by appointment</span>
              </div>
            </div>
            <div className="Contact_infoCard__qjKO4">
              <div className="Contact_iconWrap__eG97J">
                <Clock size={24} />
              </div>
              <div>
                <h4>Timings</h4>
                <p>Monday-Friday: 17:00 PM - 20:00 PM</p>
                <p>Sunday: 10:00 AM - 14:30 PM</p>
                <span className="Contact_meta__TOiAi">Saturday: Closed</span>
              </div>
            </div>
          </div>

          <div className="Contact_formCol">
            <div className="Contact_formWrapper__d7ZBB">
              {isSuccess ? (
                <motion.div
                  className="Contact_successMessage__amTop"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <CheckCircle size={48} className="Contact_successIcon__7rQq7" />
                  <h3>Thank You!</h3>
                  <p>Your message has been received successfully. CCA Arendra Nath Sharma or our team will reach out to you within 24 hours.</p>
                  <button
                    onClick={() => setIsSuccess(false)}
                    className="btn-secondary"
                    style={{ marginTop: '24px' }}
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="Contact_contactForm__Ap2lK">
                  <h3>Send us a message</h3>
                  <p className="Contact_formDesc__KSK00">
                    Fill out the form below and we'll get back to you to schedule your pre-counselling conversation.
                  </p>
                  <div className="Contact_formGroup__vaj0k">
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="Contact_formRow__0vJjb">
                    <div className="Contact_formGroup__vaj0k">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91"
                      />
                    </div>
                    <div className="Contact_formGroup__vaj0k">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                  <div className="Contact_formGroup__vaj0k">
                    <label htmlFor="background">Age / Class / Profession *</label>
                    <input
                      type="text"
                      id="background"
                      name="background"
                      required
                      value={formData.background}
                      onChange={handleChange}
                      placeholder="e.g., 16 / Class 11 / Science Stream"
                    />
                  </div>
                  <div className="Contact_formGroup__vaj0k">
                    <label htmlFor="concern">Your Primary Concern *</label>
                    <textarea
                      id="concern"
                      name="concern"
                      required
                      rows="4"
                      value={formData.concern}
                      onChange={handleChange}
                      placeholder="Tell us briefly what you'd like guidance on..."
                    />
                  </div>
                  {error && <p className="Contact_formError__2MCvG" role="alert">{error}</p>}
                  <button type="submit" className="btn-primary Contact_submitBtn__c-Tcb" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="Contact_mapSection__thtdP">
            <h2>Find Us</h2>
            <p>C-703, Tower-C, The Grace, Sector-B, Sushant Golf City, Lucknow-226030</p>
            <div className="Contact_mapWrapper__SuGJQ">
              <iframe
                title="Vitae Careers Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28495.09733645541!2d80.95801151083982!3d26.77986640000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399be4b1b758765f%3A0x4610da452614ced1!2sThe%20Grace!5e0!3m2!1sen!2sin!4v1749467007756!5m2!1sen!2sin"
                width="100%"
                height="320"
                style={{ border: 0, display: 'block', borderRadius: 'var(--radius-lg)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container Contact_portalCta__N480O">
          <h2>Ready to start right now?</h2>
          <p>Skip the wait and book your assessment directly through our secure Edumilestones portal.</p>
          <a
            href="https://vitaecareer.edumilestones.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ marginTop: '24px' }}
          >
            Go to Assessment Portal ->
          </a>
        </div>
      </section>
    </motion.div>
  );
};

export default Contact;